import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import moment from 'moment';

// Pure, presentational render of the public challenge landing page. It takes the
// SAME `data` shape the public API returns ({ brand, challenge, cohort }) and
// renders every section from `challenge.landingPage`. It owns NO data fetching,
// NO join-modal state, and NO head/meta side effects — those live in the
// data-fetching wrapper (index.js). This lets the SAME markup power both the
// live public page AND the builder's live preview (which feeds it in-progress
// form state and renders it `contained`, not as a fixed full-screen overlay).
//
// All landingPage text is PLAIN and rendered as escaped React children — never
// dangerouslySetInnerHTML.

// Whole days from now until `date` (>=0). null when there's no date.
function daysUntil(date) {
  if (!date) return null;
  const ms = new Date(date).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.ceil(ms / 86400000);
}
function fmtDate(date, tz) {
  if (!date) return '';
  const m = tz ? moment(date).tz?.(tz) || moment(date) : moment(date);
  return m.format('MMM D');
}

// "$297" or "$149.50" (cents in, the page's currency).
export function formatMoney(cents, currency = 'usd') {
  const n = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: String(currency || 'usd').toUpperCase(),
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch (_) {
    return `$${n}`;
  }
}

// A live ticking countdown to `target`. Renders nothing once the deadline
// passes (or the date is missing/invalid) so a closed cohort simply drops it.
function Countdown({ target, label }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = target ? new Date(target).getTime() - now : NaN;
  if (isNaN(ms) || ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const cells = [
    [Math.floor(total / 86400), 'days'],
    [Math.floor((total % 86400) / 3600), 'hrs'],
    [Math.floor((total % 3600) / 60), 'min'],
    [total % 60, 'sec'],
  ];
  return (
    <div className='pcl-countdown'>
      {label ? <div className='pcl-cd-label'>{label}</div> : null}
      <div className='pcl-cd-cells'>
        {cells.map(([n, u]) => (
          <div key={u} className='pcl-cd-cell'>
            <span className='pcl-cd-num'>{String(n).padStart(2, '0')}</span>
            <span className='pcl-cd-unit'>{u}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Waitlist capture modal. Owns its own form state so it renders identically on
// the live page AND in the builder preview (the reason it lives in the View,
// not the wrapper). `onSubmit(email, firstName)` returns a promise — the live
// page POSTs; the preview resolves so the flow is fully visible.
function WaitlistModal({
  open,
  onClose,
  onSubmit,
  challengeName,
  buttonLabel,
  successMessage,
  full,
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setDone(false);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const em = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) {
      setError('Enter a valid email address.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      if (typeof onSubmit === 'function') await onSubmit(em, name.trim());
      setDone(true);
    } catch (_) {
      setError("Couldn't add you. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className='pcl-modal-overlay'
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className='pcl-modal'
        role='dialog'
        aria-modal='true'
        aria-label={`Join the ${challengeName} waitlist`}
      >
        <button
          type='button'
          className='pcl-modal-x'
          onClick={onClose}
          aria-label='Close'
        >
          ✕
        </button>
        {done ? (
          <div>
            <div className='pcl-eyebrow'>// you’re on the list</div>
            <h3 className='pcl-modal-title'>You’re in.</h3>
            <p className='pcl-modal-sub'>
              {successMessage ||
                'I’ll email you the moment doors open. Keep an eye on your inbox.'}
            </p>
            <button
              type='button'
              className='pcl-cta pcl-modal-cta'
              onClick={onClose}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className='pcl-eyebrow'>// join the waitlist</div>
            <h3 className='pcl-modal-title'>{challengeName}</h3>
            <p className='pcl-modal-sub'>
              {full
                ? 'This round is full. Drop your details and I’ll email you when the next one opens.'
                : 'Doors aren’t open yet. Drop your details and I’ll email you the moment they are.'}
            </p>
            <label className='pcl-modal-label'>Name</label>
            <input
              className='pcl-modal-input'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Your name'
              autoComplete='name'
              autoFocus
            />
            <label className='pcl-modal-label'>Email</label>
            <input
              className='pcl-modal-input'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='you@email.com'
              type='email'
              autoComplete='email'
            />
            {error ? <div className='pcl-modal-error'>{error}</div> : null}
            <button
              type='submit'
              className='pcl-cta pcl-modal-cta'
              disabled={busy}
            >
              {busy ? 'Adding you…' : buttonLabel || 'Notify me when it opens'}{' '}
              <span aria-hidden>→</span>
            </button>
            <div className='pcl-modal-foot'>
              {full
                ? 'No spam — just one email when the next round opens.'
                : 'No spam — just one email when doors open.'}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// A section wrapper with an eyebrow "// LABEL" and a big title.
function Section({ eyebrow, title, children, className }) {
  return (
    <section className={`pcl-section ${className || ''}`}>
      {eyebrow ? <div className='pcl-eyebrow'>// {eyebrow}</div> : null}
      {title ? <h2 className='pcl-h2'>{title}</h2> : null}
      {children}
    </section>
  );
}

/**
 * @param {Object}  data        { brand, challenge, cohort } — the public view
 * @param {boolean} contained   render inline (relative) instead of fixed overlay
 * @param {Function} onJoin     called when a CTA is clicked (opens the join flow)
 */
export default function PublicChallengeView({ data, contained, onJoin, onWaitlist }) {
  const { brand, challenge, cohort } = data;
  const lp = challenge.landingPage || {};
  const theme = lp.theme || 'editorial';
  const sec = lp.sectionsEnabled || {};
  const on = (key) => sec[key] !== false; // absent → on
  const headline = (lp.hero && lp.hero.headline) || challenge.name;
  const heroCover = (lp.hero && lp.hero.coverImageUrl) || challenge.coverImage;
  // The "A N-week challenge" label. A trainer-set hero.kicker overrides the
  // plan-derived default everywhere it appears (hero, announcement bar, masthead).
  const kickerOverride = (lp.hero && lp.hero.kicker) || null;
  const heroKicker =
    kickerOverride ||
    (challenge.durationWeeks ? `A ${challenge.durationWeeks}-week challenge` : null);
  const mastMeta =
    kickerOverride ||
    (challenge.durationWeeks ? `${challenge.durationWeeks}-week challenge` : null);
  const ctaLabel = challenge.ctaLabel || 'Join the challenge';
  const coach = (brand && brand.name) || null;
  const days = cohort ? daysUntil(cohort.startDate) : null;
  // A capped round that has no spots left is closed to new joins (the page
  // offers the waitlist for the next round instead).
  const full = !!(cohort && cohort.full);
  const canJoin = !!(cohort && cohort.canJoin) && !full;
  // Waitlist replaces the CTA in the not-joinable states (coming-soon / doors-
  // closed / full) unless the trainer turned it off. A joinable cohort wins.
  // With the doors on "waitlist" (no round shown yet) it's always on: the email
  // form is the page's only call to action.
  const wl = lp.waitlist || {};
  const waitlistOn = !canJoin && (challenge.doors === 'waitlist' || wl.enabled !== false);
  const waitlistLabel =
    wl.buttonLabel || (full ? 'Get notified about the next round' : 'Notify me when it opens');
  // A PAID challenge: the price (+ optional payment plan / value stack). Joining
  // goes through checkout; the CTA carries the price.
  const offer = challenge.offer || null;
  const price = offer ? formatMoney(offer.priceCents, offer.currency) : null;
  const spots = challenge.spots || null;
  const spotsCopy =
    spots && canJoin
      ? `${spots.left} of ${spots.total} spot${spots.total === 1 ? '' : 's'} left`
      : null;

  // Paint the CHALLENGE OWNER's brand on the page itself — the accent is the
  // trainer whose challenge this is, never the viewer's ambient app theme.
  const brandColors = (brand && brand.colors) || null;
  const brandPrimary = Array.isArray(brandColors)
    ? brandColors[0]
    : brandColors && brandColors.primary;
  const rootStyle = brandPrimary
    ? { '--primary': brandPrimary, '--pcl-accent': brandPrimary }
    : undefined;

  const [wlOpen, setWlOpen] = useState(false);

  // The hero fills the first screen: the viewport minus whatever sits above it
  // (the announcement bar, plus the masthead under the editorial theme). Their
  // height changes with the theme, the text and wrapping, so measure it into
  // --pcl-above-hero, which the hero's min-height subtracts (index.css).
  const topbarRef = useRef(null);
  const mastheadRef = useRef(null);
  const [aboveHero, setAboveHero] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      // Fractional heights (not offsetHeight's rounding) so the hero ends
      // exactly at the fold, with no 1px sliver of the next section.
      const hOf = (el) => (el ? el.getBoundingClientRect().height : 0);
      const h = Math.round((hOf(topbarRef.current) + hOf(mastheadRef.current)) * 100) / 100;
      setAboveHero((prev) => (prev === h ? prev : h));
    };
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      if (topbarRef.current) ro.observe(topbarRef.current);
      if (mastheadRef.current) ro.observe(mastheadRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [theme]);

  const handleJoin = () => {
    if (typeof onJoin === 'function') onJoin();
  };

  const Cta = ({ label }) => {
    if (canJoin) {
      return (
        <button type='button' className='pcl-cta' onClick={handleJoin}>
          {label || ctaLabel}
          {price ? <span className='pcl-cta-price'>{price}</span> : null}
          <span aria-hidden>→</span>
        </button>
      );
    }
    if (waitlistOn) {
      return (
        <button type='button' className='pcl-cta' onClick={() => setWlOpen(true)}>
          {waitlistLabel} <span aria-hidden>→</span>
        </button>
      );
    }
    return (
      <div className='pcl-cta pcl-cta-closed' aria-disabled>
        {full ? 'This round is full' : cohort ? 'Doors are closed' : 'Next round coming soon'}
      </div>
    );
  };

  const startCopy = cohort
    ? days === 0
      ? 'Starting now'
      : `Starts ${fmtDate(cohort.startDate, cohort.timezone)}`
    : null;
  const closeCopy =
    cohort && cohort.signupClosesAt && canJoin
      ? `Doors close ${fmtDate(cohort.signupClosesAt, cohort.timezone)}`
      : null;
  // Live countdown: to doors-close if that's still ahead, else to the start.
  // Only while doors are open — a closed cohort shows no timer.
  const closesAhead =
    cohort && cohort.signupClosesAt &&
    new Date(cohort.signupClosesAt).getTime() > Date.now();
  const cdTarget = canJoin
    ? closesAhead
      ? cohort.signupClosesAt
      : cohort && cohort.startDate
    : null;
  const cdLabel = closesAhead ? 'Doors close in' : 'Starts in';

  // The announcement-bar text (also duplicated below so dark-athletic can loop
  // it as a seamless marquee; other themes center it and hide the duplicate).
  const topbarText =
    (kickerOverride
      ? kickerOverride.toUpperCase()
      : challenge.durationWeeks
      ? `A ${challenge.durationWeeks}-WEEK CHALLENGE`
      : 'A CHALLENGE') +
    (coach ? ` BY ${coach.toUpperCase()}` : '') +
    (cohort && days != null
      ? days === 0
        ? ' · STARTING NOW'
        : ` · STARTS IN ${days} DAY${days === 1 ? '' : 'S'}`
      : '');

  return (
    <div
      className={`pcl-root${contained ? ' pcl-contained' : ''}`}
      data-theme={theme}
      style={{ ...(rootStyle || {}), '--pcl-above-hero': `${aboveHero}px` }}
    >
      {/* Sticky announcement bar (a scrolling marquee under dark-athletic). */}
      <div className='pcl-topbar' ref={topbarRef}>
        <div className='pcl-topbar-track'>
          <span className='pcl-topbar-txt'>{topbarText}</span>
          <span className='pcl-topbar-txt' aria-hidden='true'>{topbarText}</span>
        </div>
      </div>

      {/* Editorial masthead — a magazine header (shown only under editorial).
          Use the trainer's logo as the wordmark when they have one; fall back
          to their name. */}
      <div className='pcl-masthead' ref={mastheadRef}>
        {brand && brand.logo ? (
          <img
            className='pcl-mast-logo'
            src={brand.logo}
            alt={coach || challenge.name}
          />
        ) : (
          <span className='pcl-mast-mark'>{coach || challenge.name}</span>
        )}
        {mastMeta ? (
          <span className='pcl-mast-meta'>{mastMeta}</span>
        ) : null}
      </div>

      {/* Hero */}
      <header className={`pcl-hero${heroCover ? '' : ' pcl-hero--solo'}`}>
        <div className='pcl-hero-inner'>
          {heroKicker ? (
            <div className='pcl-hero-kicker'>{heroKicker}</div>
          ) : null}
          <h1 className='pcl-h1'>{headline}</h1>
          {lp.hero && lp.hero.subheadline ? (
            <p className='pcl-hero-sub'>{lp.hero.subheadline}</p>
          ) : challenge.description ? (
            <p className='pcl-hero-sub'>{challenge.description}</p>
          ) : null}
          {on('countdown') && cdTarget ? (
            <Countdown target={cdTarget} label={cdLabel} />
          ) : null}
          <div className='pcl-hero-cta'>
            <Cta />
          </div>
          {(startCopy || closeCopy || spotsCopy) && (
            <div className='pcl-hero-meta'>
              {[spotsCopy, closeCopy, startCopy].filter(Boolean).join('  ·  ')}
            </div>
          )}
        </div>
        {heroCover ? (
          <div
            className='pcl-hero-cover'
            style={{ backgroundImage: `url(${heroCover})` }}
          />
        ) : null}
      </header>

      {/* Pull-quote */}
      {on('pullQuote') && lp.pullQuote && lp.pullQuote.text ? (
        <section className='pcl-pull'>
          <p className='pcl-pull-txt'>{lp.pullQuote.text}</p>
        </section>
      ) : null}

      {/* Stats */}
      {on('stats') && lp.stats && lp.stats.length ? (
        <div className='pcl-stats'>
          {lp.stats.map((s, i) => (
            <div key={i} className='pcl-stat'>
              <div className='pcl-stat-value'>{s.value}</div>
              {s.label ? <div className='pcl-stat-label'>{s.label}</div> : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* Problem / positioning */}
      {on('problem') && lp.problem && (lp.problem.headline || lp.problem.body) ? (
        <Section eyebrow='why monday never works' title={lp.problem.headline || null}>
          {lp.problem.body ? <p className='pcl-text'>{lp.problem.body}</p> : null}
          {lp.problem.tags && lp.problem.tags.length ? (
            <div className='pcl-tags'>
              {lp.problem.tags.map((t, i) => {
                // A chip prefixed with "*" is highlighted (the "bad" cycle steps).
                const bad = typeof t === 'string' && t.charAt(0) === '*';
                return (
                  <span key={i} className={`pcl-tag${bad ? ' pcl-tag--bad' : ''}`}>
                    {bad ? t.slice(1).trim() : t}
                  </span>
                );
              })}
            </div>
          ) : null}
          {lp.problem.footnote ? (
            <p className='pcl-text pcl-problem-foot'>{lp.problem.footnote}</p>
          ) : null}
        </Section>
      ) : null}

      {/* How it works — scannable roadmap above the detailed curriculum */}
      {on('howItWorks') &&
      lp.howItWorks &&
      lp.howItWorks.steps &&
      lp.howItWorks.steps.length ? (
        <Section eyebrow='how it works' title={lp.howItWorks.headline || null}>
          <div className='pcl-steps'>
            {lp.howItWorks.steps.map((s, i) => (
              <div key={i} className='pcl-step'>
                <div className='pcl-step-num'>{String(i + 1).padStart(2, '0')}</div>
                {s.label ? <div className='pcl-step-label'>{s.label}</div> : null}
                {s.title ? <div className='pcl-step-title'>{s.title}</div> : null}
                {s.body ? <p className='pcl-step-body'>{s.body}</p> : null}
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {/* Weekly rules — automatic from the challenge's scoring rules (member
          wording), so the page can never drift from how the round is scored. The
          trainer authors only the headline/intro and can hide the section. */}
      {on('rules') && data.challenge && data.challenge.rules && data.challenge.rules.length ? (
        <Section
          eyebrow='the rules'
          title={(lp.rules && lp.rules.headline) || 'What you’ll do each week'}
          className='pcl-rules'
        >
          {lp.rules && lp.rules.body ? <p className='pcl-text'>{lp.rules.body}</p> : null}
          <div className='pcl-list-box'>
            <div className='pcl-list-head pcl-accent'>Every week</div>
            {data.challenge.rules.map((r) => (
              <div key={r.key} className='pcl-list-row'>
                <strong className='pcl-rule-label'>{r.label}.</strong> {r.text}
              </div>
            ))}
          </div>
          <p className='pcl-rules-note'>
            Each week you get a score for how many of these you hit. The leaderboard
            ranks on that consistency — never on weight.
          </p>
        </Section>
      ) : null}

      {/* Curriculum */}
      {on('curriculum') && lp.curriculum && lp.curriculum.length ? (
        <Section eyebrow="here's what you get" title='The offer.'>
          <div className='pcl-curriculum'>
            {lp.curriculum.map((c, i) => (
              <div key={i} className='pcl-curr-item'>
                <div className='pcl-curr-num'>{String(i + 1).padStart(2, '0')}</div>
                <div className='pcl-curr-body'>
                  {c.title ? <h3 className='pcl-h3'>{c.title}</h3> : null}
                  {c.kicker ? <div className='pcl-curr-kicker'>{c.kicker}</div> : null}
                  {c.body ? <p className='pcl-text'>{c.body}</p> : null}
                  {c.covering && c.covering.length ? (
                    <div className='pcl-list-box'>
                      <div className='pcl-list-head pcl-accent'>WE'RE COVERING</div>
                      {c.covering.map((x, j) => (
                        <div key={j} className='pcl-list-row'>{x}</div>
                      ))}
                    </div>
                  ) : null}
                  {c.leaveKnowing && c.leaveKnowing.length ? (
                    <div className='pcl-list-box pcl-list-box-alt'>
                      <div className='pcl-list-head'>YOU'LL LEAVE KNOWING</div>
                      {c.leaveKnowing.map((x, j) => (
                        <div key={j} className='pcl-list-row'>{x}</div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {/* What's included */}
      {on('included') &&
      lp.included &&
      (lp.included.headline || (lp.included.items && lp.included.items.length)) ? (
        <Section eyebrow='everything included' title={lp.included.headline || null}>
          {lp.included.items && lp.included.items.length ? (
            <div className='pcl-included'>
              {lp.included.items.map((it, i) => (
                <div key={i} className='pcl-inc-item'>
                  {it.title ? (
                    <h3 className='pcl-inc-title'>
                      {it.emoji ? (
                        <span className='pcl-inc-emoji' aria-hidden='true'>{it.emoji}</span>
                      ) : null}
                      {it.title}
                    </h3>
                  ) : null}
                  {it.body ? <p className='pcl-inc-body'>{it.body}</p> : null}
                </div>
              ))}
            </div>
          ) : null}
          {lp.included.footnote ? (
            <div className='pcl-inc-foot'>{lp.included.footnote}</div>
          ) : null}
        </Section>
      ) : null}

      {/* Walk-away results */}
      {on('results') &&
      lp.results &&
      (lp.results.headline || (lp.results.items && lp.results.items.length)) ? (
        <Section eyebrow='the finish line' title={lp.results.headline || null}>
          {lp.results.items && lp.results.items.length ? (
            <div className='pcl-results-grid'>
              {lp.results.items.map((it, i) => (
                <div key={i} className='pcl-res-item'>
                  {it.emoji ? (
                    <div className='pcl-res-emoji' aria-hidden='true'>{it.emoji}</div>
                  ) : null}
                  {it.title ? <div className='pcl-res-title'>{it.title}</div> : null}
                  {it.body ? <div className='pcl-res-body'>{it.body}</div> : null}
                </div>
              ))}
            </div>
          ) : null}
        </Section>
      ) : null}

      {/* Community / leaderboard — "you're not doing this alone" */}
      {on('community') &&
      lp.community &&
      (lp.community.headline ||
        lp.community.body ||
        (lp.community.points && lp.community.points.length)) ? (
        <Section eyebrow="you're not doing this alone" title={lp.community.headline || null}>
          {lp.community.body ? <p className='pcl-text'>{lp.community.body}</p> : null}
          {lp.community.points && lp.community.points.length ? (
            <div className='pcl-community'>
              {lp.community.points.map((p, i) => (
                <div key={i} className='pcl-community-point'>{p}</div>
              ))}
            </div>
          ) : null}
        </Section>
      ) : null}

      {/* About the coach */}
      {on('about') && lp.about && (lp.about.headline || lp.about.body) ? (
        <Section eyebrow='the person teaching you this' className='pcl-about'>
          <div
            className={`pcl-about-grid${
              lp.about.photoUrl ? '' : ' pcl-about-grid--solo'
            }`}
          >
            {lp.about.photoUrl ? (
              <div
                className='pcl-about-photo'
                style={{ backgroundImage: `url(${lp.about.photoUrl})` }}
              />
            ) : null}
            <div className='pcl-about-body'>
              {lp.about.headline ? <h2 className='pcl-h2'>{lp.about.headline}</h2> : null}
              {lp.about.body ? <p className='pcl-text'>{lp.about.body}</p> : null}
            </div>
          </div>
        </Section>
      ) : null}

      {/* The honest truth — expectation-setting reframe */}
      {on('honesty') && lp.honesty && (lp.honesty.headline || lp.honesty.body) ? (
        <Section eyebrow='the honest truth' title={lp.honesty.headline || null}>
          {lp.honesty.body ? <p className='pcl-text'>{lp.honesty.body}</p> : null}
        </Section>
      ) : null}

      {/* For you if / not for you if */}
      {on('qualifier') &&
      ((lp.forYouIf && lp.forYouIf.length) ||
        (lp.notForYouIf && lp.notForYouIf.length)) ? (
        <Section title='Read this before you join.'>
          <div className='pcl-qual'>
            {lp.forYouIf && lp.forYouIf.length ? (
              <div className='pcl-qual-col'>
                <div className='pcl-list-head pcl-accent'>// THIS IS FOR YOU IF</div>
                {lp.forYouIf.map((x, i) => (
                  <div key={i} className='pcl-qual-row'>✓ {x}</div>
                ))}
              </div>
            ) : null}
            {lp.notForYouIf && lp.notForYouIf.length ? (
              <div className='pcl-qual-col'>
                <div className='pcl-list-head pcl-danger'>// THIS IS NOT FOR YOU IF</div>
                {lp.notForYouIf.map((x, i) => (
                  <div key={i} className='pcl-qual-row pcl-qual-no'>✗ {x}</div>
                ))}
              </div>
            ) : null}
          </div>
        </Section>
      ) : null}

      {/* Proof, in screenshots — image gallery of real results */}
      {on('proof') && lp.proof && lp.proof.images && lp.proof.images.length ? (
        <Section eyebrow='proof, in screenshots' title={lp.proof.headline || null}>
          <div className='pcl-proof-grid'>
            {lp.proof.images.map((im, i) => (
              <figure key={i} className='pcl-proof-item'>
                <img
                  className='pcl-proof-img'
                  src={im.url}
                  alt={im.caption || 'Proof screenshot'}
                  loading='lazy'
                />
                {im.caption ? (
                  <figcaption className='pcl-proof-cap'>{im.caption}</figcaption>
                ) : null}
              </figure>
            ))}
          </div>
          {lp.proof.footnote ? (
            <div className='pcl-proof-foot'>{lp.proof.footnote}</div>
          ) : null}
        </Section>
      ) : null}

      {/* Testimonials */}
      {on('testimonials') && lp.testimonials && lp.testimonials.length ? (
        <Section eyebrow='receipts' title='Proof.'>
          <div className='pcl-testimonials'>
            {lp.testimonials.map((t, i) => (
              <div key={i} className='pcl-testimonial'>
                {t.quote ? <p className='pcl-quote'>“{t.quote}”</p> : null}
                <div className='pcl-testimonial-by'>
                  {t.author ? <span className='pcl-author'>{t.author}</span> : null}
                  {t.resultLabel ? (
                    <span className='pcl-result pcl-accent'> · {t.resultLabel}</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {/* The price — automatic from the challenge's offer, so the page and the
          checkout always agree. Shown whenever the challenge is paid. */}
      {offer ? (
        <Section
          eyebrow='your spot'
          title={offer.stack && offer.stack.length ? 'Everything you get.' : 'Join the next round.'}
          className='pcl-offer'
        >
          <div className='pcl-offer-card'>
            {offer.label ? <div className='pcl-offer-badge'>{offer.label}</div> : null}
            {offer.stack && offer.stack.length ? (
              <ul className='pcl-stack'>
                {offer.stack.map((it, i) => (
                  <li key={i} className='pcl-stack-row'>
                    <span className='pcl-stack-name'>{it.name}</span>
                    {it.value ? <span className='pcl-stack-value'>{it.value}</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className='pcl-price'>
              {offer.compareAtCents ? (
                <span className='pcl-price-was'>
                  <span className='pcl-sr'>Regular price </span>
                  {formatMoney(offer.compareAtCents, offer.currency)}
                </span>
              ) : null}
              <span className='pcl-price-now'>{price}</span>
            </div>
            {offer.splitPay ? (
              <div className='pcl-price-split'>
                or {offer.splitPay.count} monthly payments of{' '}
                {formatMoney(offer.splitPay.amountCents, offer.currency)}
              </div>
            ) : null}
            <div className='pcl-offer-cta'>
              <Cta />
            </div>
            <div className='pcl-offer-foot'>
              {waitlistOn ? (
                full
                  ? 'This round is full. Join the waitlist and you’ll hear when the next one opens.'
                  : cohort
                  ? 'Sign-ups aren’t open right now. Join the waitlist and you’ll get an email when they open.'
                  : 'Doors aren’t open yet. Join the waitlist and you’ll get an email when they open.'
              ) : (
                <>
                  {spotsCopy ? <span className='pcl-spots'>{spotsCopy}</span> : null}
                  {spotsCopy ? '  ·  ' : null}
                  Secure checkout with Stripe
                </>
              )}
            </div>
          </div>
        </Section>
      ) : null}

      {/* The guarantee — trainer-written (effort-based by default). */}
      {on('guarantee') && lp.guarantee && (lp.guarantee.headline || lp.guarantee.body) ? (
        <Section eyebrow='the guarantee' className='pcl-guarantee'>
          <div className='pcl-guarantee-box'>
            <div className='pcl-guarantee-seal' aria-hidden='true'>✓</div>
            <div className='pcl-guarantee-body'>
              {lp.guarantee.headline ? <h2 className='pcl-h2'>{lp.guarantee.headline}</h2> : null}
              {lp.guarantee.body ? <p className='pcl-text'>{lp.guarantee.body}</p> : null}
            </div>
          </div>
        </Section>
      ) : null}

      {/* FAQ */}
      {on('faq') && lp.faq && lp.faq.length ? (
        <Section title='Questions.'>
          <div className='pcl-faq'>
            {lp.faq.map((f, i) => (
              <details key={i} className='pcl-faq-item'>
                <summary className='pcl-faq-q'>{f.header}</summary>
                <div className='pcl-faq-a'>{f.text}</div>
              </details>
            ))}
          </div>
        </Section>
      ) : null}

      {/* Last call */}
      {on('lastCall') && lp.lastCall && (lp.lastCall.headline || lp.lastCall.body) ? (
        <Section eyebrow='last call' className='pcl-lastcall'>
          {lp.lastCall.headline ? <h2 className='pcl-h2'>{lp.lastCall.headline}</h2> : null}
          {lp.lastCall.body ? <p className='pcl-text'>{lp.lastCall.body}</p> : null}
          <div className='pcl-hero-cta'><Cta label={ctaLabel} /></div>
          {(startCopy || closeCopy || spotsCopy) && (
            <div className='pcl-hero-meta'>
              {[spotsCopy, closeCopy, startCopy].filter(Boolean).join('  ·  ')}
            </div>
          )}
        </Section>
      ) : null}

      {/* P.S. */}
      {on('ps') && lp.ps && lp.ps.body ? (
        <Section eyebrow='p.s. if you only read one thing' className='pcl-ps'>
          <p className='pcl-text'>{lp.ps.body}</p>
          <div className='pcl-hero-cta'><Cta label={ctaLabel} /></div>
        </Section>
      ) : null}

      <footer className='pcl-footer'>
        © {new Date().getFullYear()} {coach || 'Nubod'} · powered by Nubod
      </footer>

      <WaitlistModal
        open={wlOpen}
        onClose={() => setWlOpen(false)}
        onSubmit={onWaitlist}
        challengeName={challenge.name}
        buttonLabel={waitlistLabel}
        successMessage={wl.successMessage}
        full={full}
      />
    </div>
  );
}
